/**
 * UserSelector.jsx — reusable user-picker used by the Contact Info tab for
 * fields like Assigned / Followers / Next Action Owner / demo hosts.
 *
 * The parent MUST pass a `users` prop with the live directory (typically
 * fetched once from `/api/users/directory`). When `users` is omitted or
 * empty the selector renders an empty-state message — there is no
 * DEMO_USERS fallback any longer.
 *
 * Supports both single and multi selection; output is a semicolon-
 * separated string of names for multi-mode (parity with CSV import)
 * or a single name string for single-mode.
 */
import React, { useMemo, useState } from "react";
import { X } from "lucide-react";

function initials(name = "") {
  return name.split(" ").slice(0, 2).map(s => (s || "")[0] || "").join("").toUpperCase();
}

/**
 * Value is always a string:
 *   - multi=true:  "Sarah Kim; Marcus Lee"    (semicolon separated — CSV parity)
 *   - multi=false: "Sarah Kim"
 */
export default function UserSelector({ value = "", onChange, multi = false, users = null, placeholder = "Pick a user…", testid }) {
  const directory = Array.isArray(users) ? users : [];
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const selected = useMemo(() => {
    if (!value) return [];
    return String(value).split(";").map(s => s.trim()).filter(Boolean);
  }, [value]);
  const commitMulti = (arr) => onChange?.(arr.join("; "));
  const add = (u) => {
    if (multi) {
      if (!selected.includes(u.name)) commitMulti([...selected, u.name]);
    } else {
      onChange?.(u.name);
    }
    // Iter 140.2 (stephanie@ehiehr.com issue e95e2ec1): close the
    // dropdown immediately after ANY selection (single or multi). The
    // menu is `position: absolute, top: 100%` so it visually covers
    // the Save / Cancel button row below — leaving it open in multi
    // mode meant reps couldn't click Save after picking their name,
    // and the field appeared not to save. To add another teammate,
    // the rep re-clicks the input which re-opens the menu.
    setOpen(false);
    setFilter("");
  };
  const remove = (name) => commitMulti(selected.filter(n => n !== name));
  const candidates = directory.filter(u => !selected.includes(u.name) && (filter === "" || u.name.toLowerCase().includes(filter.toLowerCase()) || (u.role || "").toLowerCase().includes(filter.toLowerCase())));

  return (
    <div data-testid={testid || "user-selector"} onClick={() => setOpen(true)} style={{ position: "relative" }}>
      <div style={{ border: "0.5px solid #ddd", borderRadius: 6, padding: "4px 6px", background: "#fff", display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center", minHeight: 30, cursor: "text" }}>
        {selected.length === 0 && !open && <span style={{ fontSize: 12, color: "#aaa", padding: "3px 4px" }}>{placeholder}</span>}
        {multi && selected.map(name => (
          <span key={name} style={{ background: "#E6F1FB", color: "#185FA5", borderRadius: 20, padding: "2px 8px", fontSize: 11, fontWeight: 500, display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 14, height: 14, background: "#185FA5", color: "#fff", borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 8, fontWeight: 700 }}>{initials(name)}</span>
            {name}
            <button data-testid={`user-selector-remove-${name.replace(/\s+/g, "-").toLowerCase()}`} onClick={e => { e.stopPropagation(); remove(name); }} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, color: "#185FA5" }}><X style={{ width: 9, height: 9 }} /></button>
          </span>
        ))}
        {!multi && selected[0] && (
          <span style={{ background: "#E6F1FB", color: "#185FA5", borderRadius: 20, padding: "2px 8px", fontSize: 12, fontWeight: 500, display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 14, height: 14, background: "#185FA5", color: "#fff", borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 8, fontWeight: 700 }}>{initials(selected[0])}</span>
            {selected[0]}
          </span>
        )}
        <input
          value={filter}
          onChange={e => { setFilter(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={selected.length === 0 ? "Type to search…" : (multi ? "Add another…" : "")}
          style={{ border: "none", outline: "none", fontSize: 12, flex: 1, minWidth: 80, background: "transparent", fontFamily: "inherit" }}
        />
      </div>
      {open && (
        <div data-testid="user-selector-menu" style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#fff", border: "0.5px solid #ddd", borderRadius: 6, marginTop: 2, maxHeight: 220, overflowY: "auto", zIndex: 20, boxShadow: "0 8px 24px rgba(0,0,0,0.08)" }}>
          {directory.length === 0 ? (
            <div data-testid="user-selector-empty" style={{ padding: "12px 14px", fontSize: 11, color: "#888" }}>
              No teammates available yet. Invite reps via Settings → Team Members.
            </div>
          ) : candidates.length === 0 ? (
            <div style={{ padding: "10px 12px", fontSize: 11, color: "#888" }}>No matches.</div>
          ) : (
            candidates.slice(0, 10).map(u => (
              <button
                key={u.id}
                data-testid={`user-selector-option-${u.id}`}
                onClick={e => { e.stopPropagation(); add(u); }}
                style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "7px 10px", border: "none", background: "transparent", cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}
                onMouseOver={e => e.currentTarget.style.background = "#f5f5f5"}
                onMouseOut={e => e.currentTarget.style.background = "transparent"}
              >
                <span style={{ width: 24, height: 24, background: "#E6F1FB", color: "#185FA5", borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700 }}>{initials(u.name)}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 500 }}>{u.name}</div>
                  {u.role && <div style={{ fontSize: 10, color: "#888" }}>{u.role}</div>}
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
